import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAssociationAuth } from "../context/AssociationAuthContext";
import NotificationBell from "./NotificationBell";
import {
  HomeIcon, SearchIcon, PlusIcon, HeartIcon, UserIcon,
  BellIcon, MessageIcon, ArrowLeftIcon
} from "./Icons";
import { UserAvatar } from "./UserAvatar";
import { StoreBadgesRow } from "./StoreBadges";
import { useUnreadCounts } from "../hooks/useUnreadCounts";
import useRetour, { retourSur } from "../hooks/useRetour";

const Logo = ({ size = "default" }) => {
  const sizes = {
    small: { svg: 32, text: "text-xl" },
    default: { svg: 48, text: "text-2xl" },
    large: { svg: 64, text: "text-3xl" }
  };
  const s = sizes[size] || sizes.default;

  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
      {/* Official brand mark — single source of truth at /logo-gively.svg.
          Do not recreate the logo inline; if the SVG ever needs to change,
          replace the file in frontend/public/. */}
      <img
        src="/logo-gively.svg"
        alt="Gively"
        width={s.svg}
        height={s.svg}
        style={{ width: s.svg, height: s.svg, display: 'block', flexShrink: 0 }}
      />
      <span className={`font-extrabold ${s.text} text-[#1DA1F2]`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        Gively
      </span>
    </Link>
  );
};

// Desktop Header with Centered Navigation

const DesktopHeader = () => {
  const { user, logout } = useAuth();
  const { account: assoAccount, logout: assoLogout } = useAssociationAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadMessages, unreadNotifications, pendingActions, clearNotifications } = useUnreadCounts(user);

  const navItems = [
    { path: "/", label: "Rechercher" },
    { path: "/publish", label: "Publier" },
    { path: "/messages", label: "Messages", requiresAuth: true, badge: unreadMessages },
    { path: "/associations", label: "Associations" },
    { path: user ? "/profile" : assoAccount ? "/association/dashboard" : "/login", label: "Mon espace", badge: user ? pendingActions : 0 },
  ];
  
  // Filter items based on auth
  const visibleNavItems = navItems.filter(item => !item.requiresAuth || user);
  
  return (
    <header className="hidden lg:block sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo size="small" />
          {/* Bouton retour intelligent desktop */}
          {location.pathname !== "/" && (
            <button
              onClick={() => {
                if (location.pathname.startsWith("/messages/")) {
                  navigate("/messages");
                } else if (location.pathname === "/messages") {
                  navigate("/");
                } else {
                  // Retour sûr (29/09/2026) : arrivée directe → accueil.
                  retourSur(navigate, "/");
                }
              }}
              className="hidden lg:flex items-center gap-1 text-slate-500 hover:text-[#1DA1F2] text-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Retour
            </button>
          )}
        </div>
        
        {/* Centered Navigation */}
        <nav className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="desktop-nav">
          {visibleNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all ${
                location.pathname === item.path || 
                (item.path === "/" && location.pathname === "/") ||
                (item.path === "/search" && location.pathname.startsWith("/search")) ||
                (item.path === "/associations" && location.pathname.startsWith("/association")) ||
                (item.path === "/messages" && location.pathname.startsWith("/messages")) ||
                (item.path === "/profile" && (location.pathname === "/profile" || location.pathname === "/dashboard" || location.pathname === "/favorites" || location.pathname === "/settings" || location.pathname === "/wallet"))
                  ? 'bg-white text-[#1DA1F2] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
              {item.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>
        
        {/* Right side actions */}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell user={user} unreadCount={unreadNotifications} onRead={clearNotifications} />
              <Link to="/profile" className="flex items-center gap-2 text-slate-600 hover:text-[#1DA1F2]" data-testid="desktop-dashboard-link">
                <UserAvatar user={user} size="md" />
              </Link>
              <button onClick={logout} className="text-sm text-slate-500 hover:text-red-500" data-testid="desktop-logout-btn">
                Déconnexion
              </button>
            </>
          ) : assoAccount ? (
            // Asso connectée : raccourci vers son espace + déconnexion asso
            // (la cloche de notifications asso vit dans le header DÉDIÉ de
            // l'espace association — DashboardPage — pour éviter un double header
            // global+dashboard sur /association/dashboard).
            <>
              <Link to="/association/dashboard" className="text-sm font-medium text-slate-600 hover:text-[#1DA1F2]" data-testid="desktop-asso-space-link">
                Mon espace asso
              </Link>
              <button onClick={assoLogout} className="text-sm text-slate-500 hover:text-red-500" data-testid="desktop-asso-logout-btn">
                Déconnexion
              </button>
            </>
          ) : (
            <Link to="/login" className="btn-primary text-sm" data-testid="desktop-login-btn">
              Connexion
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

// Bottom Navigation (Mobile)

const BottomNavigation = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Reuse the AssociationAuthProvider's already-fetched account — do NOT
  // fire a duplicate /associations/auth/me request here. The previous code
  // hit that endpoint on every mount and produced repeat 401s in logs for
  // regular (non-association) users.
  const { unreadMessages, pendingActions } = useUnreadCounts(user);

  // Reuse the AssociationAuthProvider's already-fetched account — do NOT
  // fire a duplicate /associations/auth/me request here.
  const { account: assoAccount } = useAssociationAuth() || {};
  const isAssoLoggedIn = Boolean(assoAccount);

  // Tunnel de paiement / checkout = écran focalisé : on masque la barre de
  // navigation du bas. Sinon le bouton PayPal (iframe SDK) passe par-dessus
  // la barre au scroll (z-index) -> conflit visuel signalé en prod.
  //
  // CONVERSATION OUVERTE (/messages/<id>) : on masque AUSSI la barre du bas.
  // C'est le standard du marché (Leboncoin, Vinted, WhatsApp, Messenger,
  // Telegram, Wallapop) : ouvrir une conversation passe en vue immersive
  // plein écran → plus de hauteur pour les messages + champ de saisie collé au
  // clavier sans barre parasite. La LISTE des conversations (/messages) garde
  // la barre (c'est un onglet de premier niveau). Barre déjà mobile-only.
  const _inOpenConversation = /^\/messages\/[^/]+/.test(location.pathname);
  if (
    location.pathname.startsWith("/payment")
    || location.pathname.startsWith("/checkout")
    || location.pathname.includes("/boost/stripe")
    // TUNNEL DE PUBLICATION : flux immersif plein écran (header + stepper + barre
    // d'action STICKY propres). Standard du marché (Vinted, Leboncoin, Wallapop,
    // Airbnb, FB Marketplace) : on masque la barre d'onglets pendant la
    // publication/édition → plus d'espace, CTA « Continuer/Publier » au pouce,
    // pas de barre parasite. Couvre /publish et /publish/:id/edit.
    || location.pathname.startsWith("/publish")
    || _inOpenConversation
  ) {
    return null;
  }

  // Espace association : quand l'utilisateur est connecté UNIQUEMENT en
  // tant qu'asso (pas de session user normale en parallèle), on affiche
  // la même barre que l'espace user — Accueil / Associations / Publier (+) /
  // Messages / Compte — mais adaptée à l'asso :
  //   * "Publier" n'apparaît que si la vente est activée (selling_enabled)
  //   * "Messages" n'apparaît que si la messagerie est activée
  //     (messaging_enabled) et pointe vers la messagerie de l'espace asso
  //   * "Compte" ouvre le dashboard asso
  const isAssoOnly = isAssoLoggedIn && !user;

  let navItems;
  if (isAssoOnly) {
    // Onglet courant du dashboard asso, lu depuis l'URL (?tab=…). Sert à
    // distinguer l'état actif de « Messages » et « Compte » qui pointent
    // tous deux vers /association/dashboard : sans ça, les DEUX boutons
    // restaient bleus en même temps. On donne donc un `active` explicite.
    const onDashboard = location.pathname === "/association/dashboard";
    const assoTab = new URLSearchParams(location.search).get("tab");
    navItems = [
      { path: "/", icon: HomeIcon, label: "Accueil" },
      { path: "/associations", icon: HeartIcon, label: "Associations" },
      ...((assoAccount?.selling_enabled || assoAccount?.rental_enabled || assoAccount?.service_enabled)
        ? [{ path: "/publish", icon: PlusIcon, label: "Publier", isCenter: true }]
        : []),
      ...(assoAccount?.messaging_enabled
        ? [{ path: "/association/dashboard?tab=messages", icon: MessageIcon, label: "Messages",
            active: onDashboard && assoTab === "messages" }]
        : []),
      { path: "/association/dashboard?tab=overview", icon: UserIcon, label: "Compte",
        active: onDashboard && assoTab !== "messages" },
    ];
  } else if (!user && !isAssoLoggedIn) {
    // DÉCISION Lucas 10/08/2026 (logique de plateforme) : un VISITEUR non
    // connecté n'a que 3 entrées — Accueil, Associations, Compte. « Publier »
    // et « Messages » n'apparaissent qu'une fois connecté (user OU asso) :
    // ces deux actions exigent un compte, les afficher à un invité menait à
    // un login sans contexte. (Remplace l'ancienne symétrie 5 items de 07/2026.)
    navItems = [
      { path: "/", icon: HomeIcon, label: "Accueil" },
      { path: "/associations", icon: HeartIcon, label: "Associations" },
      { path: "/login", icon: UserIcon, label: "Compte" },
    ];
  } else {
    navItems = [
      { path: "/", icon: HomeIcon, label: "Accueil" },
      { path: "/associations", icon: HeartIcon, label: "Associations" },
      { path: "/publish", icon: PlusIcon, label: "Publier", isCenter: true },
      { path: "/messages", icon: MessageIcon, label: "Messages", badge: unreadMessages },
      // Show "Mon Asso" only if on an association page AND logged in as asso
      // Otherwise show "Profil" for logged-in users
      (isAssoLoggedIn && location.pathname.startsWith("/association"))
        ? { path: "/association/dashboard", icon: UserIcon, label: "Mon Asso" }
        : { path: "/profile", icon: UserIcon, label: "Profil", badge: user ? pendingActions : 0 },
    ];
  }

  const visibleNavItems = navItems.filter(item => !item.requiresAuth || user);

  // Barre en colonnes égales (flex-1) sans slot vide : la barre user/invité a
  // toujours 5 items (Accueil · Associations · Publier · Messages · Compte) →
  // « Publier » tombe pile au centre. (Avant : un équilibrage par placeholders
  // invisibles créait un trou disgracieux en déconnecté — corrigé.)
  const slots = visibleNavItems;

  const renderSlot = (item, key) => {
    // Placeholder invisible (équilibrage) : occupe une colonne, rien d'affiché.
    if (!item) return <div key={`ph-${key}`} className="flex-1" aria-hidden="true" />;

    const cmpPath = item.matchPath || (item.path || "").split("?")[0];
    const isActive = item.active !== undefined ? item.active : (
      location.pathname === cmpPath ||
      location.pathname.startsWith(cmpPath + '/') ||
      (cmpPath === '/' && location.pathname === '/') ||
      (cmpPath === '/search' && location.pathname.startsWith('/search')) ||
      (cmpPath === '/associations' && location.pathname.startsWith('/association') && !location.pathname.startsWith('/association/dashboard')));

    if (item.isCenter) {
      // Bouton central : flex-1 (même largeur que les autres slots) → tombe au
      // centre exact. Cercle remonté ; label aligné avec les autres en bas.
      return (
        <Link key={item.path} to={item.path} className="flex-1 flex flex-col items-center justify-end" data-testid="nav-publish">
          <div className="w-14 h-14 -mt-6 bg-[#1DA1F2] rounded-full flex items-center justify-center shadow-lg ring-4 ring-white">
            <item.icon className="w-7 h-7 text-white" aria-hidden="true" />
          </div>
          <span className="text-xs mt-1 text-[#1DA1F2] font-semibold">{item.label}</span>
        </Link>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`bottom-nav-item flex-1 relative ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <div className="relative flex items-center justify-center">
          {/* Indicateur d'onglet actif Material 3 : pastille arrondie derrière
              l'icône (repère visuel fort + accessible aux daltoniens, qui ne
              voient pas le seul changement de couleur). */}
          {isActive && (
            <span aria-hidden="true"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-7 rounded-full bg-[#1DA1F2]/15 transition-all" />
          )}
          <item.icon className="w-5 h-5 relative z-10" aria-hidden="true" />
          {item.badge > 0 && (
            <span className="absolute -top-2 -right-2 z-20 bg-red-500 text-white text-xs font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          )}
        </div>
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <nav className="bottom-nav lg:hidden" data-testid="bottom-navigation">
      <div className="flex items-end max-w-lg mx-auto">
        {slots.map((item, i) => renderSlot(item, i))}
      </div>
    </nav>
  );
};

// Mobile Header with Notification Bell (shown on all pages)

const MobileHeader = ({ title, showBack = false, onBack, transparent = false, blur = false, repli = "/" }) => {
  const { user } = useAuth();
  const retour = useRetour(repli);
  // Reuse the shared unread-counts poller — no independent interval here.
  // Previously MobileHeader polled /notifications/unread-count every 60s on
  // top of DesktopHeader and BottomNavigation, producing 3x the traffic.
  const { unreadNotifications, clearNotifications } = useUnreadCounts(user);
  // `blur` : header fixe translucide (bg blanc 80% + backdrop-blur) pour les
  // pages à dégradé — garde le titre LISIBLE et FIXE sans que le contenu
  // scrollé ne passe par-dessus (anti-collision), tout en laissant deviner
  // le dégradé. `transparent` reste pour la home (logo seul, pas de collision).
  const bgClass = blur
    ? "bg-white/80 backdrop-blur-md border-b"
    : transparent
    ? ""
    : "bg-white border-b";
  return (
    <div className={`lg:hidden sticky top-0 z-40 px-4 py-3 ${bgClass}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showBack && (
            // Retour : si l'utilisateur est arrivé par lien direct / app TWA (pas
            // d'historique interne), navigate(-1) QUITTERAIT l'app. On retombe alors
            // sur l'accueil (HIG/Material : un back ne doit jamais sortir de l'app).
            // `history.length` comptait aussi les pages d'AUTRES sites vues dans
            // l'onglet : on s'appuie désormais sur React Router (useRetour).
            <button onClick={onBack || retour} data-testid="mobile-back-btn" aria-label="Retour">
              <ArrowLeftIcon className="w-6 h-6 text-slate-600" aria-hidden="true" />
            </button>
          )}
          {/* Titre de la barre de navigation mobile : ce n'est PAS le titre
              principal du document (le <h1> unique vit dans le contenu de la
              page). Le rendre en <h1> créait un second h1 sur toutes les pages
              utilisant MobileHeader → SEO + a11y cassés. */}
          {title && <div className="font-bold text-lg">{title}</div>}
          {!title && !showBack && <Logo size="small" />}
        </div>
        {user && <NotificationBell user={user} unreadCount={unreadNotifications} onRead={clearNotifications} />}
      </div>
    </div>
  );
};

// Réseaux sociaux — composant réutilisable (footer général + footer asso).
// SVG intégrés (pas de dépendance image externe), couleur de marque au survol.
const SocialLinks = ({ className = "" }) => (
  <div className={`flex justify-center items-center gap-5 ${className}`}>
    <a
      href="https://www.linkedin.com/company/assomart/"
      target="_blank" rel="noopener noreferrer"
      aria-label="Gively sur LinkedIn"
      className="text-slate-400 hover:text-[#0A66C2] transition-colors"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    </a>
    <a
      href="https://www.instagram.com/gively.fr"
      target="_blank" rel="noopener noreferrer"
      aria-label="Gively sur Instagram"
      className="text-slate-400 hover:text-[#E4405F] transition-colors"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    </a>
    <a
      href="https://www.facebook.com/share/1D1dQsnkLa/"
      target="_blank" rel="noopener noreferrer"
      aria-label="Gively sur Facebook"
      className="text-slate-400 hover:text-[#1877F2] transition-colors"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
      </svg>
    </a>
  </div>
);

// Footer légal (obligatoire en France)
const LegalFooter = () => {
  const location = useLocation();
  // Phase 11.9 — sur demande Lucas (mai 2026), le footer légal n'est
  // affiché QUE sur la home (/) et la page /associations. Partout
  // ailleurs (messagerie, profil, listing, etc.) il polluait visuellement
  // sans valeur ajoutée.
  // Match strict pour éviter d'attraper des sous-routes :
  //   /                  → OK
  //   /associations      → OK
  //   /associations/abc  → OK (page detail asso, on garde le footer)
  //   /messages          → KO
  //   /admin/...         → KO
  const path = location.pathname;
  const isHome = path === "/";
  const isAssoSection = path === "/associations" || path.startsWith("/associations/");
  if (!isHome && !isAssoSection) return null;

  // Footer ÉPURÉ (retour Lucas juin 2026 : « une marketplace n'est pas un site
  // vitrine »). On ne garde au 1er plan que ce qui aide l'utilisateur (Aide,
  // Contact, Presse) + les réseaux sociaux. Les liens institutionnels (Blog,
  // Newsletter, Qui sommes-nous, Charte, Partenaires) et l'identité légale
  // (SIREN/INPI) sont retirés de l'écran — l'identité vit sur /mentions-legales,
  // et tout le légal est regroupé dans Profil → Paramètres → Informations légales.
  const linkClass = "text-slate-500 hover:text-[#1DA1F2] transition-colors";
  // « En savoir plus » — l'essentiel utile, mis en avant.
  const PRIMARY_LINKS = [
    { to: "/aide", label: "Aide" },
    { to: "/contact", label: "Contact" },
    { to: "/presse", label: "Presse" },
    // 15/10/2026 (Lucas : « la page partenaire, elle est où ? ») : seule porte
    // d'entrée visible jusqu'ici, un petit « En savoir plus » sur /associations.
    { to: "/association/devenir-partenaire", label: "Associations : devenir partenaire" },
  ];
  return (
    <footer className="bg-white border-t border-slate-100 mt-auto pb-20 lg:pb-4">
      <div className="container mx-auto max-w-5xl px-4 py-3">
        <SocialLinks className="mb-2" />
        {/* L'application Gively — badges OFFICIELS (artwork non modifié), même
            source que l'invitation aux applis (10/10/2026 : audience des stores). */}
        <StoreBadgesRow className="mb-2" />

        <nav
          aria-label="En savoir plus"
          className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-[13px] leading-tight font-medium"
        >
          {PRIMARY_LINKS.map((l, i) => (
            <React.Fragment key={l.to}>
              {i > 0 && <span className="text-slate-300" aria-hidden="true">·</span>}
              <Link to={l.to} className={linkClass}>{l.label}</Link>
            </React.Fragment>
          ))}
        </nav>

        {/* Une seule ligne légale (Lucas, 23/10/2026 : « pollution visuelle ») : le
            détail vit sur /informations-legales, à un clic, pour tout visiteur. */}
        <nav
          aria-label="Informations légales"
          className="mt-2 flex justify-center items-center gap-x-2 text-[11px] leading-tight text-slate-400"
        >
          <span>© {new Date().getFullYear()} Gively</span>
          <span className="text-slate-200" aria-hidden="true">·</span>
          <Link to="/informations-legales" className="hover:text-slate-600 transition-colors">Informations légales</Link>
          <span className="text-slate-200" aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("gively:open-cookie-settings"))}
            className="hover:text-slate-600 transition-colors"
          >
            Cookies
          </button>
        </nav>
      </div>
    </footer>
  );
};

export { Logo, DesktopHeader, BottomNavigation, MobileHeader, LegalFooter, SocialLinks };
